import React, { useCallback, useState } from 'react';
import { Button, message, Modal } from 'antd';
import { useTranslation, Trans } from 'react-i18next';
import { PlusOutlined } from '@ant-design/icons';
import { useRiskCalculator } from '../../hooks/useRiskCalculator';
import { useCreateRiskCalculatorShare } from '../../hooks/useShare';
import {
  transformApiResponseToResultRows,
  enrichModalSelections,
} from '../../utils/transformers';
import ResultsHeader from './ResultsHeader';
import ResultsTable from './ResultsTable';
import ScenarioModal from './ScenarioModal';
import RiskSymbolsLegends from '@client/components/ui/RiskSymbolsLegends';
import bio from '../../../../../img/product/bio.svg';
import editIcon from '../../../../../img/edit-icon.svg';
import CalculateIcon from '../../../../../img/client/icons/calculate_action.svg';

/**
 * ResultsScreen Component
 * Main container for displaying risk calculation results
 * Matches Figma design with header, instruction text, table, legend, and add button
 *
 * @param {Array} results - Array of ResultRow objects
 * @param {Function} setResults - Function to update results
 */
const ResultsScreen = ({ results, setResults }) => {
  const { t } = useTranslation('riskCalculator');
  const { reset, goToForm, selections, enrichedData } = useRiskCalculator();

  // Share creation mutation
  const { mutateAsync: createShare, isPending: isCreatingShare } =
    useCreateRiskCalculatorShare();

  // Modal state for edit/add/duplicate scenarios
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: 'add', // 'add' | 'edit' | 'duplicate'
    editingRowId: null,
    initialData: null,
  });

  // Delete confirmation modal state
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, row: null });

  // Reset confirmation modal state
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  // Handler for reset button - show confirmation modal
  const handleReset = useCallback(() => {
    setIsResetModalOpen(true);
  }, []);

  const confirmReset = useCallback(() => {
    setIsResetModalOpen(false);
    reset();
    message.success(t('reset_confirm_title'));
  }, [reset, t]);

  // State for PDF loading
  const [pdfLoading, setPdfLoading] = useState(false);

  // Handler for print button - generates PDF via backend and opens in new tab
  const handlePrint = useCallback(async () => {
    setPdfLoading(true);
    try {
      const csrfToken = document.cookie
        .split('; ')
        .find((row) => row.startsWith('csrftoken='))
        ?.split('=')[1];
      const response = await fetch('/api/active-substances/risk-indices/calculate/pdf/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken && { 'X-CSRFToken': csrfToken }),
        },
        body: JSON.stringify({ results }),
      });
      if (!response.ok) throw new Error('PDF generation failed');
      const blob = await response.blob();
      const pdfUrl = window.URL.createObjectURL(blob);
      window.open(pdfUrl, '_blank');
    } catch (err) {
      console.error('PDF generation failed:', err);
      message.error(t('print_error', 'Erreur lors de la génération du PDF'));
    } finally {
      setPdfLoading(false);
    }
  }, [results, t]);

  // Handler for share button - create share and copy URL to clipboard
  const handleShare = useCallback(async () => {
    try {
      // Create share with input data, results, and enriched data
      const share = await createShare({
        inputData: selections,
        resultsData: results,
        enrichedData: enrichedData,
      });

      // Build the share URL
      const shareUrl = `${window.location.origin}/calculateur-indices-risque/s/${share.token}/`;

      // Copy to clipboard
      await navigator.clipboard.writeText(shareUrl);
      message.success({
        content: t('share_created_copied'),
        duration: 10,
        closable: true,
      });
      return true;
    } catch (error) {
      console.error('Failed to create share:', error);
      message.error({
        content: t('share_error'),
        duration: 10,
      });
      return false;
    }
  }, [createShare, selections, results, enrichedData, t]);

  // Handler for add scenario button
  const handleAddScenario = useCallback(() => {
    setModalState({
      isOpen: true,
      mode: 'add',
      editingRowId: null,
      initialData: null,
    });
  }, []);

  // Handler for modal close
  const handleModalClose = useCallback(() => {
    setModalState({
      isOpen: false,
      mode: 'add',
      editingRowId: null,
      initialData: null,
    });
  }, []);

  // Handler for modal submit - transform API response and update results
  const handleModalSubmit = useCallback(
    async (apiData, selections, modalEnrichedData, mode) => {
      // SP-442 — enrich the modal selections via bulk-initialize so Copy /
      // "Ajouter un scénario" rows get the same hovers (active substances,
      // culture height, listed dosages, Bio / Prescription) as the main flow.
      let enriched = modalEnrichedData;
      try {
        enriched = await enrichModalSelections(selections);
      } catch (err) {
        console.error('[Risk Calculator] modal enrichment failed:', err);
      }

      // Transform API response into ResultRow objects using shared utility
      const newResultRows = transformApiResponseToResultRows(
        apiData,
        selections,
        enriched
      );

      if (mode === 'edit') {
        // Replace the edited row
        const newResults = results.map((r) =>
          r.id === modalState.editingRowId ? newResultRows[0] : r
        );
        setResults(newResults);
        message.success(t('save_success'));
      } else {
        // Add or duplicate - append new rows
        setResults([...results, ...newResultRows]);
        message.success(t('save_success'));
      }
    },
    [results, setResults, modalState.editingRowId, t]
  );

  // Handler for edit action
  const handleEdit = useCallback((row) => {
    setModalState({
      isOpen: true,
      mode: 'edit',
      editingRowId: row.id,
      initialData: row,
    });
  }, []);

  // Handler for duplicate action
  const handleDuplicate = useCallback((row) => {
    setModalState({
      isOpen: true,
      mode: 'duplicate',
      editingRowId: null,
      initialData: row,
    });
  }, []);

  // Handler for delete action - show confirmation modal
  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, row });
  }, []);

  const confirmDelete = useCallback(() => {
    const row = deleteConfirm.row;
    setDeleteConfirm({ isOpen: false, row: null });

    const newResults = results.filter((r) => r.id !== row.id);

    if (newResults.length === 0) {
      message.info(t('validation_complete_previous_steps'));
      goToForm();
    } else {
      setResults(newResults);
      message.success(t('action_delete'));
    }
  }, [deleteConfirm.row, results, setResults, goToForm, t]);

  // Loading state (future enhancement)
  const isLoading = false;

  return (
    <div className="results-screen">
      {/* Header with title and action buttons */}
      <ResultsHeader
        onReset={handleReset}
        onPrint={handlePrint}
        onShare={handleShare}
      />

      {/* Instruction text and Legend section */}
      <div className="flex flex-col gap-4 mb-4">
        {/* Instruction text - full width */}
        <div className="flex flex-col items-start gap-2 p-6 rounded-2xl bg-[#FAFAFA] w-full">
          <p className="text-xs sm:text-sm text-gray-600 m-0">
            <Trans i18nKey="results_instruction" ns="riskCalculator" />
          </p>
        </div>

        {/* Legend section */}
        <div className="flex justify-end">
          <div className="flex flex-col justify-center items-start gap-4 py-4 px-6 rounded-2xl border border-[#F0F0F0] bg-[#FAFAFA] w-full sm:w-fit">
            <p className="text-[#000] text-[14px] font-bold m-0">{t('legend_title')}</p>
            <div className="flex gap-4 items-center flex-wrap">
              <p className="text-[#000] text-[14px] font-medium m-0">{t('legend_labels')}</p>
              <img src={bio} alt="biologique" style={{ height: 28 }} />
              <div className="flex h-[28px] w-[28px] justify-center items-center rounded bg-[#FF4D4F]">
                <img src={editIcon} alt="edit" className="w-4 h-4" />
              </div>
            </div>
            <RiskSymbolsLegends title={t('legend_risk_symbols')} />
          </div>
        </div>
      </div>

      {/* Results Table - full width on mobile with horizontal scroll */}
      <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
        <ResultsTable
          data={results || []}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          loading={isLoading}
        />
      </div>

      {/* Add Scenario Button - centered */}
      <div className="flex justify-center mt-6 px-4 sm:px-0">
        <Button
          type="primary"
          onClick={handleAddScenario}
          className="w-full sm:w-auto h-11 sm:h-[50px] px-4 sm:px-6 text-sm sm:text-base"
          icon={<img src={CalculateIcon} alt="" className="w-5 h-5" />}
        >
          {t('btn_add_scenario')}
        </Button>
      </div>

      {/* Scenario Modal for Edit/Add/Duplicate */}
      <ScenarioModal
        isOpen={modalState.isOpen}
        mode={modalState.mode}
        initialData={modalState.initialData}
        parentEnrichedData={enrichedData}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        title={<span className="text-base sm:text-lg">{t('confirm_delete_title')}</span>}
        open={deleteConfirm.isOpen}
        onOk={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, row: null })}
        okText={t('confirm_ok')}
        cancelText={t('reset_confirm_cancel')}
        width="90vw"
        style={{ maxWidth: 500 }}
      >
        <p className="text-sm sm:text-base">{t('confirm_delete_message')}</p>
      </Modal>

      {/* Reset Confirmation Modal */}
      <Modal
        title={<span className="text-base sm:text-lg">{t('confirm_reset_title')}</span>}
        open={isResetModalOpen}
        onOk={confirmReset}
        onCancel={() => setIsResetModalOpen(false)}
        okText={t('reset_confirm_ok')}
        cancelText={t('reset_confirm_cancel')}
        okButtonProps={{ danger: true }}
        width="90vw"
        style={{ maxWidth: 500 }}
      >
        <p className="text-sm sm:text-base">{t('confirm_reset_message')}</p>
      </Modal>
    </div>
  );
};

export default ResultsScreen;
